import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { getLocaleBasedExperimentVariantsFromName } from '@change-corgi/fcm/utils';

import { usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';

type GdprCopy = Readonly<{
	prompt: string;
	opt_in: string;
	opt_out: string;
	opt_out_warning: string;
	form_error: string;
}>;

export type GdprConfig = Readonly<{
	enabled: boolean;
	overridden: boolean;
	copy: GdprCopy;
	/**
	 * True when sign_form_gdpr_radio_removal_experiment resolved to `v1`. Hides the marketing-consent
	 * radios (GdprConsentField) and forces the submitted consent to opt-out (normalizeGdprConsent) —
	 * consent is still recorded, just not chosen by the user.
	 * Deliberately kept out of `enabled`, which also drives the "Do not display my name" checkbox in
	 * BottomOptionsFields — unchanged for v1.
	 */
	radiosRemoved: boolean;
}>;

export function useGdprConfig(): GdprConfig {
	const {
		data: {
			experiments: { gdprSignOptInCopy: fcmExperiment, signFormGdprRadioRemovalExperiment },
		},
	} = usePetitionDetailsPageUserData();
	const {
		data: { gdprConsentProvided },
	} = usePetitionDetailsPageUserData();

	const { translate } = useI18n();

	return useMemo(() => {
		const copyOverride =
			fcmExperiment.config.copy &&
			getLocaleBasedExperimentVariantsFromName(fcmExperiment.config.copy, fcmExperiment.experimentName)?.[
				fcmExperiment.variation
			];

		const copy = {
			prompt: '',
			opt_in: translate('fe.sign_form.marketing_consent.opt_in'),
			opt_out: translate('fe.sign_form.marketing_consent.opt_out'),
			opt_out_warning: translate('fe.sign_form.marketing_consent.opt_out_warning'),
			form_error: translate('fe.sign_form.marketing_consent.form_error'),
			...copyOverride,
		};
		const enabled = fcmExperiment.config.enabled && !gdprConsentProvided;
		return {
			enabled,
			overridden: !!copyOverride,
			copy,
			radiosRemoved: signFormGdprRadioRemovalExperiment.variation === 'v1',
		};
	}, [fcmExperiment, gdprConsentProvided, signFormGdprRadioRemovalExperiment, translate]);
}
